import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid2';
import CalculateIcon from '@mui/icons-material/Calculate';
import RadioField from './fields/RadioField';
import SliderField from './fields/SliderField';
import DropdownField from './fields/DropdownField';
import Results from './Results';
import '../widgetStyles.css';
// Added Material UI table and collapse imports:
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import { Box as MuiBox } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

// Updated CollapsibleRow component:
const CollapsibleRow = ({ row, odd }) => {
  const [open, setOpen] = useState(false);
  const background = odd ? '#f5f5f5' : 'inherit';
  return (
    <>
      <TableRow style={{ backgroundColor: background }}>
        <TableCell
          style={{
            border: '1px solid #ccc',
            padding: '8px',
            fontWeight: 'bold',
            width: '50%',
            boxSizing: 'border-box'
          }}
        >
          {row.name}
        </TableCell>
        <TableCell
          style={{
            border: '1px solid #ccc',
            padding: '8px',
            width: '50%',
            boxSizing: 'border-box'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <span>{row.value}</span>
            <IconButton size="small" onClick={() => setOpen(!open)}>
              <HelpOutlineIcon />
            </IconButton>
          </div>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={2}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <MuiBox margin={2}>
              {row.explanation}
            </MuiBox>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const evaluateCondition = (condition, answers) => {
  if (!condition) return true;
  try {
    const func = new Function('answers', `return (${condition});`);
    return func(answers);
  } catch (error) {
    console.error('Error evaluating condition:', condition, error);
    return false;
  }
};

const Widget = (props) => {
  const { bgcolor, primarycolor } = props;
  const project = props.project;
  const config = require(`../configs/${project}.json`);
  const { calculate } = require(`../calculations/${project}`);

  const widgetTitle = config.widgetTitle || "Calculator";
  const questions = config.questions || config;

  const initialAnswers = questions.reduce((acc, { id, default: defaultValue }) => {
    acc[id] = defaultValue || '';
    return acc;
  }, {});

  const [answers, setAnswers] = useState(initialAnswers);
  const [result, setResult] = useState({ status: '', items: [], message: '', color: '', title: '' });
  const [loading, setLoading] = useState(true);

  const handleAnswerChange = (id, value) => {
    setAnswers((prev) => ({ ...prev, [id]: value }));
  };

  // Call async calculation when answers change.
  useEffect(() => {
    async function getResult() {
      setLoading(true);
      // Pass 'data' prop as opts.data if present and project is solarCost
      let calcResult;
      if (project === 'solarCost' && props.data) {
        calcResult = await calculate({ ...answers, primarycolor }, { data: props.data });
      } else {
        calcResult = await calculate({ ...answers, primarycolor });
      }
      setResult(calcResult);
      setLoading(false);
    }
    getResult();
  }, [answers, props.data, project]);

  const ICON_STYLE = {
    fontSize: '34px',
    color: primarycolor ? primarycolor : '#023a51',
    marginRight: '10px'
  };

  // Set CSS variable for primary color
  const containerStyle = {
    backgroundColor: bgcolor,
    '--primary-color': primarycolor || '#229626'
  };

  return (
    <Box id="bq-widget" style={containerStyle}>
      <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        <Grid container justifyContent="center" spacing={2}>
          <Grid size={{ xs: 12, md: 8 }}>
            <Box className="bq-widget-title">
              <CalculateIcon style={ICON_STYLE} />
              <h3>{widgetTitle}</h3>
            </Box>

            {questions.map(({ id, condition, fieldType, ...questionProps }) => {
              if (!evaluateCondition(condition, answers)) return null;

              let FieldComponent;
              if (fieldType === 'slider') {
                FieldComponent = SliderField;
              } else if (fieldType === 'dropdown') {
                FieldComponent = DropdownField;
              } else {
                FieldComponent = RadioField;
              }

              return (
                <FieldComponent
                  key={id}
                  {...questionProps}
                  answer={answers[id]}
                  updateAnswer={(value) => handleAnswerChange(id, value)}
                  xs={4}
                  sm={4}
                  primarycolor={primarycolor}
                />
              );
            })}
          </Grid>

          <Grid size={{ xs: 12, md: 4 }} className="bq-results">
            <Results result={result} loading={loading} />
          </Grid>
        </Grid>

        {result.table && (
          <TableContainer component={Paper} style={{ marginTop: '20px' }}>
            <Table style={{ borderCollapse: 'collapse', tableLayout: 'fixed', width: '100%' }}>
              {/* Removed TableHead */}
              <TableBody>
                {result.table.map((row, idx) => (
                  <CollapsibleRow key={idx} row={row} odd={idx % 2 !== 0} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </div>
    </Box>
  );
};

export default Widget;